import { useEffect, useRef, useState } from 'react';

const products = [
  {
    name: 'ELOPAR VPN',
    tagline: 'Secure. Private. Fast.',
    description:
      'A powerful VPN solution for iOS that encrypts your internet connection, hides your IP address, and lets you browse the web privately and securely from anywhere in the world.',
    highlights: ['256-bit AES Encryption', 'No-Log Policy', '50+ Server Locations', 'Kill Switch Protection'],
    image:
      'https://public.readdy.ai/ai/img_res/bc726fda4675d763df7f04d630faa0f3.jpg',
    accent: 'from-sky-500 to-cyan-400',
    badge: 'VPN',
  },
  {
    name: 'ELOPAR Cleaner',
    tagline: 'Clean. Fast. Optimized.',
    description:
      'An intelligent iOS cleaner that scans your device for junk files, duplicate photos, large files, and unnecessary caches — freeing up storage space and boosting performance.',
    highlights: ['Smart Junk Detection', 'Duplicate Photo Finder', 'Large File Manager', 'Memory Optimizer'],
    image:
      'https://readdy.ai/api/search-image?query=modern%20iOS%20cleaner%20app%20interface%20mockup%20on%20iPhone%20with%20light%20clean%20background%20showing%20storage%20optimization%20charts%20and%20clean%20minimal%20UI%20with%20fresh%20green%20and%20white%20color%20scheme%20professional%20app%20screenshot&width=600&height=700&seq=cleaner-product-1&orientation=portrait',
    accent: 'from-emerald-500 to-teal-400',
    badge: 'Cleaner',
  },
];

function ProductCard({
  product,
  index,
}: {
  product: (typeof products)[0];
  index: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="flex flex-col lg:flex-row gap-10 items-center"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(40px)',
        transition: `opacity 0.7s ease ${index * 0.15}s, transform 0.7s ease ${index * 0.15}s`,
      }}
    >
      {/* Image */}
      <div
        className={`relative flex-shrink-0 w-full lg:w-72 h-80 lg:h-96 rounded-3xl overflow-hidden bg-gradient-to-br ${
          index % 2 === 0 ? 'lg:order-1' : 'lg:order-2'
        }`}
        style={{ background: 'linear-gradient(135deg, #0d1b2a 0%, #0f2744 100%)' }}
      >
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover object-top"
        />
        <div className={`absolute top-4 left-4 bg-gradient-to-r ${product.accent} text-white text-xs font-bold tracking-widest uppercase px-3 py-1.5 rounded-full`}>
          {product.badge}
        </div>
      </div>

      {/* Content */}
      <div className={`flex-1 ${index % 2 === 0 ? 'lg:order-2' : 'lg:order-1'}`}>
        <span className={`text-xs font-semibold tracking-[3px] uppercase bg-gradient-to-r ${product.accent} bg-clip-text text-transparent mb-3 block`}>
          iOS Application
        </span>
        <h3
          className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-2"
          style={{ fontFamily: "'Syne', sans-serif" }}
        >
          {product.name}
        </h3>
        <p className="text-gray-400 font-medium text-base mb-5 italic">{product.tagline}</p>
        <p className="text-gray-600 text-base leading-relaxed mb-8 max-w-lg">{product.description}</p>

        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {product.highlights.map((h) => (
            <li key={h} className="flex items-center gap-3">
              <div className={`w-5 h-5 flex items-center justify-center rounded-full bg-gradient-to-br ${product.accent} flex-shrink-0`}>
                <i className="ri-check-line text-white text-xs" />
              </div>
              <span className="text-gray-700 text-sm font-medium">{h}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function ProductsSection() {
  const titleRef = useRef<HTMLDivElement>(null);
  const [titleVisible, setTitleVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setTitleVisible(true); },
      { threshold: 0.2 }
    );
    if (titleRef.current) observer.observe(titleRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="products" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div
          ref={titleRef}
          className="text-center mb-20"
          style={{
            opacity: titleVisible ? 1 : 0,
            transform: titleVisible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <span className="text-xs font-semibold tracking-[3px] uppercase text-sky-500 mb-3 block">
            Our Products
          </span>
          <h2
            className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight"
            style={{ fontFamily: "'Syne', sans-serif" }}
          >
            iOS Applications
            <br />
            <span className="text-gray-400 font-light">Crafted with Purpose</span>
          </h2>
        </div>

        {/* Products */}
        <div className="flex flex-col gap-20 md:gap-28">
          {products.map((p, i) => (
            <ProductCard key={p.name} product={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
